import DataGrid, {
    FilterRow, Toolbar,
    Export, ColumnChooser, ColumnFixing, Column, LoadPanel, GroupPanel, Paging, Pager,
    Scrolling, HeaderFilter, SearchPanel
} from '../../components/BootstrapDataGrid';
import Axios from "../../api/axios";
import notify from '../../utils/notifyUtil';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Dict from "../../messages.js";
import * as utl from '../../utils/utils.js';
import * as glb from "../../globals.js";
import { useTabActions } from '../../contexts/tab-navigation';
import CariKart from "./carikart.js";
import CariKartTab from "./carikart-tab.js";
import CariSatis from "./carisatis.js";
import IslemGirPanel from "./IslemGirPanel.js";
import ResimGirPanel from "./ResimGirPanel.js";
import ParaGirPanel from "./ParaGirPanel.js";
import AnemnezGirPanel from "./AnemnezGirPanel.js";
import AramaGirPanel from "./AramaGirPanel.js";
import EkimKanalAlimGirPanel from "./EkimKanalAlimGirPanel.js";
import { generateCariPDF } from '../../utils/cariPdf.js';
import { readCariListLaunch, clearCariListLaunch } from '../../utils/cariListLaunch.js';
import { warmCariBundleFromIdb, ensureCariBundlesCached } from '../../services/syncService';
import {
    addLocalDays,
    formatPointWallClock,
    startOfLocalDay,
} from '../../utils/businessDateValues.js';
import { canDeleteByOwner, cariDataSahibiRef, FOREIGN_DELETE_MESSAGE } from '../../utils/ownRecordDelete.js';

export function resetCarilistCache() {
    // Liste artık sunucudan gelir; sync/IndexedDB list cache yok.
    // API uyumu için bırakıldı (filtre değişiminde çağrılıyor).
}

const DATE_FIELD_ITEMS = [
    { value: 'CAR_GIR_TAR', text: 'Giriş Tarihi' },
    { value: 'CAR_SS_META_TAR', text: 'Meta Tarihi' },
    { value: 'CD_ILERI_ARA', text: 'İleri Arama Tarihi' },
    { value: 'CD_RAN_TAR', text: 'Randevu Tarihi' },
];

const PERIOD_ITEMS = [
    { value: 'none', text: 'Tarih Filtresi Yok' },
    { value: 'month', text: 'Ay' },
    { value: 'year', text: 'Yıl' },
    { value: 'custom', text: 'Özel Tarih' },
];

export function buildCariDateRange(periodType, anchor, customFrom, customTo) {
    if (!periodType || periodType === 'none') return null;
    if (periodType === 'custom') {
        if (!customFrom || !customTo) return null;
        const from = startOfLocalDay(customFrom);
        const toExclusive = addLocalDays(startOfLocalDay(customTo), 1);
        return {
            from: formatPointWallClock(from),
            to: formatPointWallClock(toExclusive),
        };
    }
    const selected = anchor ? new Date(anchor) : new Date();
    if (periodType === 'year') {
        return {
            from: formatPointWallClock(new Date(selected.getFullYear(), 0, 1)),
            to: formatPointWallClock(new Date(selected.getFullYear() + 1, 0, 1)),
        };
    }
    return {
        from: formatPointWallClock(new Date(selected.getFullYear(), selected.getMonth(), 1)),
        to: formatPointWallClock(new Date(selected.getFullYear(), selected.getMonth() + 1, 1)),
    };
}

export default function CariList() {
    // Tek satırda otomatik seçim için
    const handleContentReady = useCallback((e) => {
        const data = e.component.getDataSource().items();
        if (data.length === 1) {
            setActiveRow(data[0]);
            e.component.selectRows([data[0].CAR_REF], false);
        }
    }, []);
    const [searchKey, setSearchKey] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const searchDebounceRef = useRef(null);
    const [dateField, setDateField] = useState('CAR_GIR_TAR');
    const [periodType, setPeriodType] = useState('none');
    const [periodAnchor, setPeriodAnchor] = useState(new Date());
    const [customFrom, setCustomFrom] = useState(null);
    const [customTo, setCustomTo] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');
    const [newArrivalOnly, setNewArrivalOnly] = useState(false);
    const [degerlendirmeFiltresi, setDegerlendirmeFiltresi] = useState(null);
    const [durumFiltresi, setDurumFiltresi] = useState(null);
    const tabNavigation = useTabActions();

    useEffect(() => () => {
        if (searchDebounceRef.current) window.clearTimeout(searchDebounceRef.current);
    }, []);

    // Ekran ve yetki kontrolü (EKR_EKRANADI büyük/küçük harf farklı olabilir)
    const configuredScreen = React.useMemo(() => glb.default.EKRANLkp
        ?.find((x) => String(x.EKR_EKRANADI || '').toLowerCase() === 'cari'), []);
    // Cari Kart tüm kullanıcılara açık: rol ekranındaki eski EKLE/DUZELT
    // değerleri butonları gizlemesin. Silme/pasifleştirme hakları değişmez.
    const screen = React.useMemo(() => ({
        ...(configuredScreen || {}),
        EKR_EKRANADI: configuredScreen?.EKR_EKRANADI || 'cari',
        EKLE: true,
        DUZELT: true,
    }), [configuredScreen]);

    const canSeeAllRecords = useCallback(() => {
        try {
            const user = glb.default?.KULLANICI || {};
            return Boolean(user.KUL_ISADMIN) || Number(user.KUL_YETKI) === 0 || utl.hasRight('TumKayitlar');
        } catch (_) { }
        return false;
    }, []);

    const isAdminUser = useCallback(() => {
        const user = glb.default?.KULLANICI || {};
        return Boolean(user.KUL_ISADMIN) || Number(user.KUL_YETKI) === 0 || Number(user.KUL_YS) === 0;
    }, []);

    // Cari liste rol ayrımı olmadan herkeste Tüm Kayıtlar ile açılır.
    const getDefaultAtamaFiltresi = () => 1;

    const resolveLaunchFilters = () => {
        const launch = readCariListLaunch();
        if (launch?.mode === 'yeniAtama') {
            return {
                atamaFiltresi: 3,
                bildirimFiltresi: { yeniAtama: true, atamaBos: false, ilgiIsteyen: false },
            };
        }
        if (launch?.mode === 'atamaBos') {
            return {
                atamaFiltresi: 1,
                bildirimFiltresi: { yeniAtama: false, atamaBos: true, ilgiIsteyen: false },
            };
        }
        if (launch?.mode === 'ilgiIsteyen') {
            return {
                atamaFiltresi: 3,
                bildirimFiltresi: { yeniAtama: false, atamaBos: false, ilgiIsteyen: true },
            };
        }
        return {
            atamaFiltresi: getDefaultAtamaFiltresi(),
            bildirimFiltresi: { yeniAtama: false, atamaBos: false, ilgiIsteyen: false },
        };
    };

    const launchFiltersRef = useRef(resolveLaunchFilters());
    const [atamaFiltresi, setAtamaFiltresi] = useState(launchFiltersRef.current.atamaFiltresi);
    const [bildirimFiltresi, setBildirimFiltresi] = useState(launchFiltersRef.current.bildirimFiltresi);
    const [formShow, setFormShow] = useState(false);
    const [formSatisShow, setFormSatisShow] = useState(false);
    const [formIslemGirShow, setFormIslemGirShow] = useState(false);
    const [formResimGirShow, setFormResimGirShow] = useState(false);
    const [formParaGirShow, setFormParaGirShow] = useState(false);
    const [formAnemnezGirShow, setFormAnemnezGirShow] = useState(false);
    const [formAramaGirShow, setFormAramaGirShow] = useState(false);
    const [formEkimKanalAlimGirShow, setFormEkimKanalAlimGirShow] = useState(false);
    const [activeRow, setActiveRow] = useState({});
    const dateRange = React.useMemo(
        () => buildCariDateRange(periodType, periodAnchor, customFrom, customTo),
        [periodType, periodAnchor, customFrom, customTo]
    );
    // CustomStore ile serverside grid – aynı filtrede tekrar istek yok (modül seviyesi sonuç)
    const listFormGridDS = React.useMemo(() => ({
        key: 'CAR_REF',
        load: async (loadOptions) => {
            const cid = glb.default?.KULLANICI?.KUL_DEF_FIR_REF;
            const kulid = glb.default?.KULLANICI?.KUL_REF;

            // Varsayılan: giriş tarihi desc (en yeni üstte; ref'ler atlar)
            const sort = (Array.isArray(loadOptions.sort) && loadOptions.sort.length)
                ? loadOptions.sort
                : [{ selector: 'CAR_GIR_TAR', desc: true }, { selector: 'CAR_REF', desc: true }];
            const params = {
                skip: loadOptions.skip ?? 0,
                take: loadOptions.take ?? 100,
                sort: JSON.stringify(sort),
                filter: loadOptions.filter ? JSON.stringify(loadOptions.filter) : '',
                st: atamaFiltresi,
                sk: searchKey,
                cid,
                kulid,
                deg: degerlendirmeFiltresi || '',
                drm: durumFiltresi || '',
                yeniAtama: bildirimFiltresi.yeniAtama ? '1' : '',
                atamaBos: bildirimFiltresi.atamaBos ? '1' : '',
                ilgiIsteyen: bildirimFiltresi.ilgiIsteyen ? '1' : '',
                ap: activeFilter === 'active' ? '0' : activeFilter === 'passive' ? '1' : '',
                newOnly: newArrivalOnly ? '1' : '',
                dateField: dateRange ? dateField : '',
                dateFrom: dateRange?.from || '',
                dateTo: dateRange?.to || '',
            };
            const res = await Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}CARI_KART/lst`, { params });
            const payload = res.data || {};
            const data = payload.data || [];
            const totalCount = payload.totalCount ?? 0;
            if (data.length > 0) setActiveRow(data[0]);
            else setActiveRow({});

            // Görünen sayfa: IDB→bellek. Arama sonuçlarında eksikler tam bundle ile cache'lenir
            // (eski CAR_GIR_TAR dahil; bağlı kayıtlarda tarih sınırı yok).
            const pageIds = [];
            for (const row of data) {
                const id = Number(row?.CAR_REF);
                if (id > 0) {
                    pageIds.push(id);
                    warmCariBundleFromIdb(id);
                }
            }
            if (String(searchKey || '').trim() && pageIds.length) {
                ensureCariBundlesCached(pageIds);
            }

            return { data, totalCount };
        }
    }), [
        atamaFiltresi, searchKey, degerlendirmeFiltresi, durumFiltresi, bildirimFiltresi,
        activeFilter, newArrivalOnly, dateField, dateRange
    ]);
    const [formDataOps, setFormDataOps] = useState(null);
    const listFormGrid = useRef(null);
    const canViewAssignedUser = isAdminUser() || canSeeAllRecords();

    const handleGeneratePDF = useCallback(() => {
        if (!checkCariSelected()) return;
        generateCariPDF(activeRow, glb, notify);
    }, [activeRow]);

    const checkCariSelected = useCallback(() => {
        if (!activeRow || !activeRow.CAR_REF) {
            notify('Cari seçili değil', 'warning', 2000);
            return false;
        }
        return true;
    }, [activeRow]);

    const searchItemsByRights = React.useMemo(() => [
        { value: 1, text: 'Tüm Kayıtlar' },
        { value: 2, text: 'Benim Girdiklerim' },
        { value: 3, text: 'Bana Atananlar' },
        { value: 4, text: 'Yetkili Olduklarım' },
        { value: 5, text: 'Yeni Atananlar' }
    ], []);

    const onListFormGridFocusedRowChanged = useCallback((e) => {
        if (e && e.row && e.row.data && e.row.data.CAR_REF !== activeRow.CAR_REF) {
            setActiveRow(e.row.data);
            const id = Number(e.row.data.CAR_REF);
            if (id > 0) warmCariBundleFromIdb(id);
        }
    }, [activeRow.CAR_REF]);

    const onRowDblClick = useCallback(() => {
    });

    const applyCariListLaunch = useCallback((launch) => {
        if (!launch) return;
        resetCarilistCache();
        clearCariListLaunch();
        if (launch.mode === 'yeniAtama') {
            // Liste açılınca silinmez — 24s birikir; arama kaydı / süre dolunca düşer
            setAtamaFiltresi(3);
            setBildirimFiltresi({ yeniAtama: true, atamaBos: false, ilgiIsteyen: false });
            return;
        }
        if (launch.mode === 'atamaBos' && canSeeAllRecords()) {
            setAtamaFiltresi(1);
            setBildirimFiltresi({ yeniAtama: false, atamaBos: true, ilgiIsteyen: false });
            return;
        }
        if (launch.mode === 'ilgiIsteyen') {
            // Liste açılınca toplu okundu YOK — cariye girip yazıştıkça düşer
            setAtamaFiltresi(3);
            setBildirimFiltresi({ yeniAtama: false, atamaBos: false, ilgiIsteyen: true });
        }
    }, [canSeeAllRecords]);

    useEffect(() => {
        applyCariListLaunch(readCariListLaunch());
        const onLaunch = (e) => applyCariListLaunch(e.detail || readCariListLaunch());
        window.addEventListener('cari-list-launch', onLaunch);
        return () => window.removeEventListener('cari-list-launch', onLaunch);
    }, [applyCariListLaunch]);

    // Bildirim listesi açıkken rozet/arama sonrası liste tazelensin
    useEffect(() => {
        const onBildirimRefresh = () => {
            if (
                !bildirimFiltresi.ilgiIsteyen
                && !bildirimFiltresi.yeniAtama
                && !bildirimFiltresi.atamaBos
            ) return;
            resetCarilistCache();
            listFormGrid.current?.instance?.getDataSource()?.reload();
        };
        window.addEventListener('cari-ilgi-bildirim-refresh', onBildirimRefresh);
        window.addEventListener('cari-atama-bildirim-refresh', onBildirimRefresh);
        return () => {
            window.removeEventListener('cari-ilgi-bildirim-refresh', onBildirimRefresh);
            window.removeEventListener('cari-atama-bildirim-refresh', onBildirimRefresh);
        };
    }, [bildirimFiltresi.ilgiIsteyen, bildirimFiltresi.yeniAtama, bildirimFiltresi.atamaBos]);

    useEffect(() => {
        const onSyncChange = () => {
            resetCarilistCache();
            listFormGrid.current?.instance?.getDataSource()?.reload();
        };
        window.addEventListener('cari-sync-updated', onSyncChange);
        window.addEventListener('cari-sync-local-removed', onSyncChange);
        return () => {
            window.removeEventListener('cari-sync-updated', onSyncChange);
            window.removeEventListener('cari-sync-local-removed', onSyncChange);
        };
    }, []);

    const canDeleteActiveCari = Number(activeRow?.CAR_REF) > 0
        && canDeleteByOwner(glb.default?.KULLANICI, cariDataSahibiRef(activeRow));

    const deleteActiveCari = async () => {
        if (!canDeleteActiveCari) {
            notify(FOREIGN_DELETE_MESSAGE, 'warning', 2500);
            return;
        }
        const ok = window.confirm(`${activeRow.CAR_ADSOY || activeRow.CAR_REF} kaydı silinsin mi?`);
        if (!ok) return;
        try {
            const user = glb.default.KULLANICI;
            await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}doalls`, {
                data: { id: activeRow.CAR_REF },
                type: 'remove',
                pnm: screen.EKR_TABLOADI || 'CARI_KART',
                uid: user.KUL_REF,
                vid: 1,
                cid: user.KUL_FIR_REF,
                defcid: user.KUL_DEF_FIR_REF,
                isource: 1,
                lang: user.KUL_DILKOD,
                inip: '??',
            });
            notify('Cari silindi.', 'success', 2000);
            listFormGrid.current?.instance?.getDataSource()?.reload();
        } catch (err) {
            const message = err?.response?.data?.message || FOREIGN_DELETE_MESSAGE;
            notify(message, 'error', 3000);
        }
    };

    if (!screen) {
        return (
            <div className="prim-empty" style={{ padding: 24 }}>
                {Dict.get('NotAuthThisScreen')}
            </div>
        );
    }

    return (
        <React.Fragment>
            {(bildirimFiltresi.ilgiIsteyen || bildirimFiltresi.yeniAtama || bildirimFiltresi.atamaBos) ? (
                <div
                    style={{
                        padding: '8px 12px',
                        marginBottom: 8,
                        background: bildirimFiltresi.ilgiIsteyen
                            ? '#f3e5f5'
                            : bildirimFiltresi.atamaBos
                                ? '#e3f2fd'
                                : '#ffebee',
                        border: `1px solid ${
                            bildirimFiltresi.ilgiIsteyen
                                ? '#ce93d8'
                                : bildirimFiltresi.atamaBos
                                    ? '#90caf9'
                                    : '#ef9a9a'
                        }`,
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        flexWrap: 'wrap',
                    }}
                >
                    <span style={{
                        fontWeight: 600,
                        color: bildirimFiltresi.ilgiIsteyen
                            ? '#6a1b9a'
                            : bildirimFiltresi.atamaBos
                                ? '#1565c0'
                                : '#c62828',
                    }}>
                        {bildirimFiltresi.ilgiIsteyen
                            ? 'Arama / bilgi isteyenler — ilk gelenden son gelene; WA cevap veya arama kaydı ile düşer (sadece bakmak düşürmez)'
                            : bildirimFiltresi.atamaBos
                                ? 'Ataması boş cariler (son 24 saat) — ilk gelenden son gelene; arama kaydı oluşunca düşer'
                                : 'Size atanan cariler (son 24 saat) — ilk gelenden son gelene; arama kaydı oluşunca düşer'}
                    </span>
                    <button
                        type="button"
                        className="dx-button dx-button-mode-outlined"
                        onClick={() => {
                            setBildirimFiltresi({ yeniAtama: false, atamaBos: false, ilgiIsteyen: false });
                            setAtamaFiltresi(getDefaultAtamaFiltresi());
                            resetCarilistCache();
                        }}
                    >
                        Filtreyi kapat
                    </button>
                </div>
            ) : null}
            <DataGrid
                className={'context dx-card wide-card'}
                dataSource={listFormGridDS || []}
                keyExpr="CAR_REF"
                remoteOperations={true}
                showBorders={true}
                allowColumnResizing={true}
                focusedRowEnabled={true}
                hoverStateEnabled={true}
                repaintChangesOnly={true}
                ref={listFormGrid}
                height={'100%'}
                width={'100%'}
                defaultFocusedRowIndex={0}
                columnAutoWidth={true}
                onRowDblClick={onRowDblClick}
                onFocusedRowChanged={onListFormGridFocusedRowChanged}
                onCellClick={(e) => {
                    if (e.rowType === 'data' && e.column.dataField === 'CAR_ADSOY') {
                        if (!e.row || !e.row.data) return;
                        const carRef = Number(e.row.data.CAR_REF);
                        if (carRef > 0) warmCariBundleFromIdb(carRef);
                        const uniqueKey = `CARI-${e.row.data.CAR_REF}`;
                        const ownsRow = canDeleteByOwner(glb.default?.KULLANICI, cariDataSahibiRef(e.row.data));
                        const ops = {
                            ptype: 'update',
                            CAR_REF: e.row.data.CAR_REF,
                            formReadOnly: !ownsRow,
                            ap: 'update',
                            crud: { saveTest: true, saveDraft: true, save: true, saveCopy: screen?.EKLE, saveClose: true, passive: false, active: false, remove: false }
                        };
                        tabNavigation.openDetailTab(uniqueKey, `${e.row.data.CAR_ADSOY || e.row.data.CAR_REF}`, CariKartTab, { ops });
                    }
                }}
                autoNavigateToFocusedRow={true}
                onContentReady={handleContentReady}
            >

                <Toolbar items={[
                    {
                        location: 'before', locateInMenu: 'auto', widget: 'dxSelectBox',
                        options: {
                            width: 145,
                            value: periodType,
                            items: PERIOD_ITEMS,
                            displayExpr: 'text',
                            valueExpr: 'value',
                            onValueChanged: (e) => setPeriodType(e.value || 'none'),
                        }
                    },
                    {
                        location: 'before', locateInMenu: 'auto', widget: 'dxSelectBox',
                        options: {
                            width: 165,
                            value: dateField,
                            items: DATE_FIELD_ITEMS,
                            displayExpr: 'text',
                            valueExpr: 'value',
                            onValueChanged: (e) => setDateField(e.value || 'CAR_GIR_TAR'),
                        }
                    },
                    {
                        visible: periodType === 'month' || periodType === 'year',
                        location: 'before', locateInMenu: 'auto', widget: 'dxDateBox',
                        options: {
                            width: 145,
                            value: periodAnchor,
                            type: 'date',
                            placeholder: periodType === 'year' ? 'Yıl seçin' : 'Ay seçin',
                            onValueChanged: (e) => setPeriodAnchor(e.value || new Date()),
                        }
                    },
                    {
                        visible: periodType === 'custom',
                        location: 'before', locateInMenu: 'auto', widget: 'dxDateBox',
                        options: {
                            width: 145,
                            value: customFrom,
                            type: 'date',
                            placeholder: 'Başlangıç',
                            onValueChanged: (e) => setCustomFrom(e.value),
                        }
                    },
                    {
                        visible: periodType === 'custom',
                        location: 'before', locateInMenu: 'auto', widget: 'dxDateBox',
                        options: {
                            width: 145,
                            value: customTo,
                            type: 'date',
                            placeholder: 'Bitiş',
                            onValueChanged: (e) => setCustomTo(e.value),
                        }
                    },
                    {
                        location: 'before', locateInMenu: 'auto', widget: 'dxSelectBox',
                        options: {
                            width: 125,
                            value: activeFilter,
                            items: [
                                { value: 'all', text: 'Aktif/Pasif Tümü' },
                                { value: 'active', text: 'Aktifler' },
                                { value: 'passive', text: 'Pasifler' },
                            ],
                            displayExpr: 'text',
                            valueExpr: 'value',
                            onValueChanged: (e) => setActiveFilter(e.value || 'all'),
                        }
                    },
                    {
                        location: 'before', locateInMenu: 'auto', widget: 'dxSelectBox',
                        options: {
                            width: 125,
                            value: newArrivalOnly ? 'new' : 'all',
                            items: [
                                { value: 'all', text: 'Tüm Cariler' },
                                { value: 'new', text: 'Yeni Gelenler (7 Gün)' },
                            ],
                            displayExpr: 'text',
                            valueExpr: 'value',
                            onValueChanged: (e) => setNewArrivalOnly(e.value === 'new'),
                        }
                    },
                    {
                        location: 'before', locateInMenu: 'auto', widget: 'dxTextBox',
                        options: {
                            value: searchInput,
                            placeholder: Dict.get('Arama Kelimesi Gir'),
                            width: '225',
                            showClearButton: true,
                            onValueChanged: (e) => {
                                const v = e.value || '';
                                setSearchInput(v);
                                if (searchDebounceRef.current) window.clearTimeout(searchDebounceRef.current);
                                searchDebounceRef.current = window.setTimeout(() => {
                                    setSearchKey(v);
                                    resetCarilistCache();
                                }, 1000);
                            },
                            onKeyDown: (e) => {
                                if (e.event && e.event.key === 'Enter') {
                                    if (searchDebounceRef.current) window.clearTimeout(searchDebounceRef.current);
                                    const v = e.component.option('value') || '';
                                    setSearchInput(v);
                                    setSearchKey(v);
                                    resetCarilistCache();
                                }
                            }
                        }
                    },
                    {
                        location: 'before', locateInMenu: 'auto', widget: 'dxSelectBox', options: {
                            width: '200',
                            value: atamaFiltresi,
                            displayExpr: "text",
                            valueExpr: "value",
                            placeholder: Dict.get('Hangi Kayıtlar Seç'),
                            items: searchItemsByRights,
                            showClearButton: true,
                            onValueChanged: (e) => {
                                setAtamaFiltresi(e.value || null);
                                setBildirimFiltresi({ yeniAtama: false, atamaBos: false });
                                resetCarilistCache();
                            }
                        }
                    },
                    // {
                    //     location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: {
                    //         icon: 'exportpdf',
                    //         hint: 'Cari Dosyası PDF',
                    //         text: 'Dosya',
                    //         type: 'default',
                    //         onClick: handleGeneratePDF
                    //     }
                    // },
                    {
                        location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: {
                            visible: screen.EKLE,
                            icon: 'add',
                            hint: Dict.get('Yeni Cari'),
                            text: Dict.get('Yeni Cari'),
                            type: 'success',
                            onClick: () => {
                                const uniqueKey = `CARI-NEW-${Date.now()}`;
                                const ops = {
                                    ptype: 'insert',
                                    CAR_REF: 0,
                                    formReadOnly: false,
                                    ap: false,
                                    crud: {
                                        saveTest: true, saveDraft: true, save: true, saveCopy: false,
                                        saveClose: true, passive: false, active: false, remove: false
                                    }
                                };
                                tabNavigation.openDetailTab(uniqueKey, Dict.get('Yeni Cari'), CariKartTab, { ops });
                            }
                        }
                    },
                    {
                        location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: {
                            visible: screen.DUZELT && canDeleteByOwner(glb.default?.KULLANICI, cariDataSahibiRef(activeRow)),
                            icon: 'edit',
                            hint: Dict.get('editHint'),
                            text: Dict.get('editHint'),
                            type: 'default',
                            onClick: () => {
                                if (!checkCariSelected()) return;
                                if (!canDeleteByOwner(glb.default?.KULLANICI, cariDataSahibiRef(activeRow))) {
                                    notify(FOREIGN_DELETE_MESSAGE, 'warning', 3000);
                                    return;
                                }
                                const carRef = Number(activeRow.CAR_REF);
                                if (carRef > 0) warmCariBundleFromIdb(carRef);
                                const uniqueKey = `CARI-${activeRow.CAR_REF}`;
                                const title = `Düzenle: ${activeRow.CAR_ADSOY || activeRow.CAR_REF}`;
                                const ops = {
                                    ptype: 'update',
                                    CAR_REF: activeRow.CAR_REF,
                                    CAR_ADSOY: activeRow.CAR_ADSOY,
                                    CAR_GSM: activeRow.CAR_GSM,
                                    CAR_TEL1: activeRow.CAR_TEL1,
                                    BENZER_VAR: activeRow.BENZER_VAR,
                                    formReadOnly: false,
                                    ap: 'update',
                                    crud: {
                                        saveTest: true, saveDraft: true, save: true, saveCopy: screen.EKLE,
                                        saveClose: true, passive: false, active: false, remove: false
                                    }
                                };
                                tabNavigation.openDetailTab(uniqueKey, title, CariKartTab, { ops });
                            }
                        }
                    },
                    // {
                    //     location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: {
                    //         icon: 'money',
                    //         hint: Dict.get('Satış'),
                    //         text: Dict.get('Satış'),
                    //         type: 'default',
                    //         onClick: () => {
                    //             // activeRow kontrolü güçlendirildi
                    //             if (!activeRow || !activeRow.CAR_REF) {
                    //                 notify('Cari seçili değil', 'warning', 2000);
                    //                 return;
                    //             }
                    //             if (formSatisShow || formDataOps) return; // Panel zaten açık veya ops atanmış
                    //             const ops = {
                    //                 CAR_REF: activeRow.CAR_REF,
                    //                 CAR_ADSOY: activeRow.CAR_ADSOY,
                    //                 CAR_GSM: activeRow.CAR_GSM,
                    //                 CAR_TEL1: activeRow.CAR_TEL1,
                    //                 BENZER_VAR: activeRow.BENZER_VAR
                    //             };
                    //             setFormDataOps(ops);
                    //             setFormSatisShow(true);
                    //         }
                    //     }
                    // },
                    // {
                    //     location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: {
                    //         icon: 'event',
                    //         hint: Dict.get('İşlem'),
                    //         text: Dict.get('İşlem'),
                    //         type: 'default',
                    //         onClick: () => {
                    //             if (!checkCariSelected()) return;
                    //             setFormIslemGirShow(true);
                    //         }
                    //     }
                    // },
                    // {
                    //     location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: {
                    //         icon: 'preferences',
                    //         hint: 'Ekim/Kanal Alım',
                    //         text: 'Ekim/Kanal Alım',
                    //         type: 'default',
                    //         onClick: () => {
                    //             if (!checkCariSelected()) return;
                    //             setFormEkimKanalAlimGirShow(true);
                    //         }
                    //     }
                    // },
                    // {
                    //     location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: {
                    //         icon: 'photo',
                    //         hint: Dict.get('Resim'),
                    //         text: Dict.get('Resim'),
                    //         type: 'default',
                    //         onClick: () => {
                    //             if (!checkCariSelected()) return;
                    //             if (formResimGirShow) return; // Panel zaten açık
                    //             setFormResimGirShow(true);
                    //         }
                    //     }
                    // },
                    // {
                    //     location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: {
                    //         icon: 'money',
                    //         hint: Dict.get('Borç/Alınan'),
                    //         text: Dict.get('Borç/Alınan'),
                    //         type: 'default',
                    //         onClick: () => {
                    //             if (!checkCariSelected()) return;
                    //             setFormParaGirShow(true);
                    //         }
                    //     }
                    // },
                    // {
                    //     location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: {
                    //         icon: 'pulldown',
                    //         hint: Dict.get('Anemnez'),
                    //         text: Dict.get('Anemnez'),
                    //         type: 'default',
                    //         onClick: () => {
                    //             if (!checkCariSelected()) return;
                    //             setFormAnemnezGirShow(true);
                    //         }
                    //     }
                    // },
                    // {
                    //     location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: {
                    //         icon: 'tel',
                    //         hint: Dict.get('Arama'),
                    //         text: Dict.get('Arama'),
                    //         type: 'default',
                    //         onClick: () => {
                    //             if (!checkCariSelected()) return;
                    //             setFormAramaGirShow(true);
                    //         }
                    //     }
                    // },
                    {
                        location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: {
                            visible: canDeleteActiveCari,
                            icon: 'trash',
                            hint: Dict.get('removeHint') || 'Sil',
                            text: 'Sil',
                            type: 'danger',
                            onClick: deleteActiveCari,
                        }
                    },
                    {
                        location: 'after', locateInMenu: 'auto', widget: 'dxButton', options: {
                            visible: screen.GOR,
                            icon: 'info',
                            hint: Dict.get('Bilgi'),
                            text: Dict.get('Bilgi'),
                            type: 'warning',
                            onClick: () => {
                                if (!checkCariSelected()) return;
                                const carRef = Number(activeRow.CAR_REF);
                                if (carRef > 0) warmCariBundleFromIdb(carRef);
                                const uniqueKey = `CARI-INFO-${activeRow.CAR_REF}`;
                                const ops = {
                                    ptype: 'info',
                                    CAR_REF: activeRow.CAR_REF,
                                    CAR_ADSOY: activeRow.CAR_ADSOY,
                                    CAR_GSM: activeRow.CAR_GSM,
                                    CAR_TEL1: activeRow.CAR_TEL1,
                                    BENZER_VAR: activeRow.BENZER_VAR,
                                    formReadOnly: true,
                                    ap: 'update',
                                    crud: { saveTest: false, saveDraft: false, save: false, saveCopy: false, saveClose: false, passive: false, active: false, remove: false }
                                };
                                tabNavigation.openDetailTab(uniqueKey, `Bilgi: ${activeRow.CAR_ADSOY || activeRow.CAR_REF}`, CariKartTab, { ops });
                            }
                        }
                    },
                    {
                        name: "exportButton", locateInMenu: "auto", location: "after", options: {
                            visible: screen.CIKTIAL,
                            hint: Dict.get('export'),
                            text: Dict.get('export'),
                            type: 'success'
                        }
                    }
                ]} />

                <Export enabled={true} fileName="Cari Kart" allowExportSelectedData={true} />
                <Scrolling useNative={true} />
                <GroupPanel visible={true} />
                <ColumnChooser enabled={true} mode={'select'} />
                <ColumnFixing enabled={false} />
                <LoadPanel enabled={true} />
                <FilterRow visible={true} applyFilter={'auto'} />
                <SearchPanel visible={false} highlightCaseSensitive={true} />
                <Paging defaultPageSize={100} />
                <Pager showPageSizeSelector={true} showInfo={true} allowedPageSizes={[25, 50, 100]} />

                <Column dataField="CAR_REF" caption={Dict.get('id')} />
                <Column dataField="isactive" caption={Dict.get('isactive')} dataType="boolean" allowFiltering={false} />
                <Column dataField="ATANAN_KULLANICI"
                    visible={canViewAssignedUser}
                    caption={Dict.get('Atanan Kullanıcı')} />
                <Column dataField="CAR_ADSOY" caption={Dict.get('Ad Soyad')}
                    cellRender={(data) => <u className="pointer">{data.displayValue}</u>} />
                <Column dataField="CAR_GIR_TAR" caption={Dict.get('Giriş Tarihi')} dataType="datetime" format="dd-MM-yyyy HH:mm" sortOrder="desc" sortIndex={0} />
                <Column dataField="SON_DEG" caption={Dict.get('Son Değerlendirme')} />
                <Column dataField="SON_DURUM" caption={Dict.get('Son Durum')} />
                <Column dataField="CAR_CINSIYET" caption={Dict.get('Cinsiyet')} />
                <Column dataField="CAR_GSM" caption={Dict.get('GSM1')} />
                <Column dataField="CAR_TEL1" caption={Dict.get('GSM2')} />
                <Column dataField="ULKE_AD" caption={Dict.get('Ülke')} />
                <Column dataField="BENZER_VAR" caption={Dict.get('Benzer')} dataType="boolean" />
                <Column dataField="SATIS_VAR" caption="Satış Var" dataType="boolean" allowFiltering={false} allowSorting={false} />
                <Column dataField="RESIM_VAR" caption="Resim Var" dataType="boolean" allowFiltering={false} allowSorting={false} />
                <Column dataField="ISLEM_VAR" caption="İşlem Var" dataType="boolean" allowFiltering={false} allowSorting={false} />
                <Column dataField="TAHSILAT_VAR" caption="Tahsilat Var" dataType="boolean" allowFiltering={false} allowSorting={false} />
                <Column dataField="EKIM_KANAL_ALIM_VAR" caption="Ekim/Kanal Alım Var" dataType="boolean" allowFiltering={false} allowSorting={false} />
            </DataGrid>

            {/* Child Forms */}
            {/* Sadece ekle/düzenle işlemlerinde CariKart render edilsin */}
            {formShow && formDataOps && (
                <CariKart
                    formShow={formShow}
                    ops={formDataOps}
                    onFormKapandi={() => { setFormShow(false); setFormDataOps(null); }}
                />
            )}

            {formSatisShow && formDataOps && (
                <CariSatis
                    formShow={formSatisShow}
                    ops={formDataOps}
                    onFormKapandi={() => { setFormSatisShow(false); setFormDataOps(null); }}
                />
            )}

            <IslemGirPanel
                carRef={activeRow.CAR_REF}
                carAdSoy={activeRow.CAR_ADSOY}
                formShow={formIslemGirShow}
                onFormKapandi={() => setFormIslemGirShow(false)}
            />

            {formResimGirShow && activeRow?.CAR_REF && (
                <ResimGirPanel
                    carRef={activeRow.CAR_REF}
                    carAdSoy={activeRow.CAR_ADSOY}
                    formShow={formResimGirShow}
                    onFormKapandi={() => setFormResimGirShow(false)}
                />
            )}

            <ParaGirPanel
                carRef={activeRow.CAR_REF}
                carAdSoy={activeRow.CAR_ADSOY}
                formShow={formParaGirShow}
                onFormKapandi={() => setFormParaGirShow(false)}
            />
            <AnemnezGirPanel
                carRef={activeRow.CAR_REF}
                carAdSoy={activeRow.CAR_ADSOY}
                visible={formAnemnezGirShow}
                onHiding={() => setFormAnemnezGirShow(false)}
            />
            <AramaGirPanel
                carRef={activeRow.CAR_REF}
                carAdSoy={activeRow.CAR_ADSOY}
                visible={formAramaGirShow}
                onHiding={() => setFormAramaGirShow(false)}
            />
            <EkimKanalAlimGirPanel
                carRef={activeRow.CAR_REF}
                carAdSoy={activeRow.CAR_ADSOY}
                formShow={formEkimKanalAlimGirShow}
                onFormKapandi={() => setFormEkimKanalAlimGirShow(false)}
            />

        </React.Fragment>
    );
}
